import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Input from './Input';
import Select from './Select';
import Button from './Button';
import DayPicker from './DayPicker';
import DateExceptionsPicker from './DateExceptionsPicker';
import Modal from './Modal';
import { Disclosure } from '@headlessui/react';

interface WindowFormData {
  name: string;
  windowType: 'monthly' | 'weekly' | 'daily';
  startDayOfMonth: string;
  startDayOfWeek: string;
  startTime: string;
  endTime: string;
  intervalMinutes: string;
  repeatDaysOfWeek: number[];
  specificDate: string;
  isRepeating: boolean;
  repeatStartDate: string;
  repeatEndDate: string;
  repeatExceptions: Date[];
  pricePerInterval: string;
  quantity: string;
}

interface ConflictWindow {
  _id: string;
  name?: string;
  windowType: string;
  startTime?: string;
  endTime?: string;
  pricePerInterval: number;
  quantity: number;
}

interface Conflict {
  window: ConflictWindow;
  reason: string;
}

interface WindowFormProps {
  mode: 'create' | 'edit';
  listingId: string;
  windowId?: string;
  initialData?: Partial<WindowFormData>;
}

const WindowForm: React.FC<WindowFormProps> = ({ mode, listingId, windowId, initialData }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<WindowFormData>({
    name: '',
    windowType: 'daily',
    startDayOfMonth: '1',
    startDayOfWeek: '0',
    startTime: '09:00',
    endTime: '17:00',
    intervalMinutes: '60',
    repeatDaysOfWeek: [],
    specificDate: '',
    isRepeating: true,
    repeatStartDate: '',
    repeatEndDate: '',
    repeatExceptions: [],
    pricePerInterval: '',
    quantity: '1',
    ...initialData,
  });

  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [windowToDelete, setWindowToDelete] = useState<string | null>(null);
  const [deletingConflict, setDeletingConflict] = useState(false);

  // Debounced conflict check
  const checkConflicts = useCallback(
    async (data: WindowFormData) => {
      if (!data.pricePerInterval || !data.quantity) return;

      setCheckingConflicts(true);
      try {
        const payload: Record<string, unknown> = {
          _id: windowId,
          windowType: data.windowType,
          startTime: data.startTime,
          endTime: data.endTime,
          pricePerInterval: parseFloat(data.pricePerInterval),
          quantity: parseInt(data.quantity),
          isRepeating: data.isRepeating,
        };

        if (data.windowType === 'monthly') {
          payload.startDayOfMonth = parseInt(data.startDayOfMonth);
        } else if (data.windowType === 'weekly') {
          payload.startDayOfWeek = parseInt(data.startDayOfWeek);
        } else if (data.windowType === 'daily') {
          payload.intervalMinutes = parseInt(data.intervalMinutes) || 0;
          if (data.isRepeating) {
            payload.repeatDaysOfWeek = data.repeatDaysOfWeek;
          } else {
            payload.specificDate = data.specificDate;
          }
        }

        if (data.name) payload.name = data.name;
        if (data.repeatStartDate) payload.repeatStartDate = data.repeatStartDate;
        if (data.repeatEndDate) payload.repeatEndDate = data.repeatEndDate;
        if (data.repeatExceptions.length > 0) payload.repeatExceptions = data.repeatExceptions;

        const response = await api.post<{ conflicts: Conflict[] }>(
          `/windows/listing/${listingId}/check-conflicts`,
          payload
        );

        setConflicts(response.conflicts || []);
      } catch (error) {
        console.error('Error checking conflicts:', error);
      } finally {
        setCheckingConflicts(false);
      }
    },
    [listingId, windowId]
  );

  // Debounce conflict checking
  useEffect(() => {
    const timer = setTimeout(() => {
      checkConflicts(formData);
    }, 300);

    return () => clearTimeout(timer);
  }, [formData, checkConflicts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload: Record<string, unknown> = {
        listingId,
        name: formData.name || undefined,
        windowType: formData.windowType,
        startTime: formData.startTime,
        endTime: formData.endTime,
        pricePerInterval: parseFloat(formData.pricePerInterval),
        quantity: parseInt(formData.quantity),
        isRepeating: formData.isRepeating,
      };

      if (formData.windowType === 'monthly') {
        payload.startDayOfMonth = parseInt(formData.startDayOfMonth);
      } else if (formData.windowType === 'weekly') {
        payload.startDayOfWeek = parseInt(formData.startDayOfWeek);
      } else if (formData.windowType === 'daily') {
        payload.intervalMinutes = parseInt(formData.intervalMinutes) || 0;
        if (formData.isRepeating) {
          payload.repeatDaysOfWeek = formData.repeatDaysOfWeek;
        } else {
          if (!formData.specificDate) {
            toast.error('Specific date is required for non-repeating windows');
            setIsLoading(false);
            return;
          }
          payload.specificDate = formData.specificDate;
        }
      }

      if (formData.repeatStartDate) payload.repeatStartDate = formData.repeatStartDate;
      if (formData.repeatEndDate) payload.repeatEndDate = formData.repeatEndDate;
      if (formData.repeatExceptions.length > 0) payload.repeatExceptions = formData.repeatExceptions;

      if (mode === 'create') {
        await api.post('/windows', payload);
        toast.success('Window created successfully!');
      } else {
        await api.put(`/windows/${windowId}`, payload);
        toast.success('Window updated successfully!');
      }

      navigate(`/listings/${listingId}/windows`);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || `Failed to ${mode} window`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConflict = async () => {
    if (!windowToDelete) return;

    setDeletingConflict(true);
    try {
      await api.delete(`/windows/${windowToDelete}`);
      toast.success('Conflicting window deleted');
      setDeleteModalOpen(false);
      setWindowToDelete(null);
      // Recheck conflicts
      checkConflicts(formData);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; hadRentals?: boolean } } };
      if (err.response?.data?.hadRentals) {
        toast.warning('Window deleted, but it had existing rentals');
      } else {
        toast.error(err.response?.data?.error || 'Failed to delete window');
      }
    } finally {
      setDeletingConflict(false);
    }
  };

  const openDeleteModal = (windowId: string) => {
    setWindowToDelete(windowId);
    setDeleteModalOpen(true);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        {/* Window Name */}
        <Input
          id="window-name"
          label="Window Name (Optional)"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Weekdays 9-5"
          helperText="Internal reference for you"
        />

        {/* Window Type */}
        <Select
          id="window-type"
          label="Window Type"
          value={formData.windowType}
          onChange={(e) =>
            setFormData({
              ...formData,
              windowType: e.target.value as 'monthly' | 'weekly' | 'daily',
            })
          }
          options={[
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
          ]}
          required
        />

        {/* Type-Specific Fields */}
        {formData.windowType === 'monthly' && (
          <Input
            id="start-day-of-month"
            label="Day of Month"
            type="number"
            min="1"
            max="31"
            value={formData.startDayOfMonth}
            onChange={(e) => setFormData({ ...formData, startDayOfMonth: e.target.value })}
            required
            helperText="Which day of the month (1-31)"
          />
        )}

        {formData.windowType === 'weekly' && (
          <Select
            id="start-day-of-week"
            label="Day of Week"
            value={formData.startDayOfWeek}
            onChange={(e) => setFormData({ ...formData, startDayOfWeek: e.target.value })}
            options={[
              { value: '0', label: 'Sunday' },
              { value: '1', label: 'Monday' },
              { value: '2', label: 'Tuesday' },
              { value: '3', label: 'Wednesday' },
              { value: '4', label: 'Thursday' },
              { value: '5', label: 'Friday' },
              { value: '6', label: 'Saturday' },
            ]}
            required
          />
        )}

        {formData.windowType === 'daily' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Input
                id="start-time"
                label="Start Time"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
              />
              <Input
                id="end-time"
                label="End Time"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
              />
            </div>

            <Input
              id="interval-minutes"
              label="Interval Minutes"
              type="number"
              min="0"
              value={formData.intervalMinutes}
              onChange={(e) => setFormData({ ...formData, intervalMinutes: e.target.value })}
              helperText="0 means entire window, otherwise specify booking interval size"
            />

            {/* Repeating Toggle */}
            <div>
              <label htmlFor="is-repeating" className="flex items-center gap-2 text-sm">
                <input
                  id="is-repeating"
                  name="is-repeating"
                  type="checkbox"
                  checked={formData.isRepeating}
                  onChange={(e) => setFormData({ ...formData, isRepeating: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-gray-700 font-medium">Repeating Window</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {formData.isRepeating
                  ? 'This window repeats on selected days'
                  : 'This window is for a specific date only'}
              </p>
            </div>

            {formData.isRepeating ? (
              <DayPicker
                label="Repeat on Days"
                selectedDays={formData.repeatDaysOfWeek}
                onChange={(days) => setFormData({ ...formData, repeatDaysOfWeek: days })}
                required
              />
            ) : (
              <Input
                id="specific-date"
                label="Specific Date"
                type="date"
                value={formData.specificDate}
                onChange={(e) => setFormData({ ...formData, specificDate: e.target.value })}
                required
                helperText="The single date this window applies to"
              />
            )}
          </>
        )}

        {/* Price and Quantity */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            id="price-per-interval"
            label="Price per Interval"
            type="number"
            step="0.01"
            min="0"
            value={formData.pricePerInterval}
            onChange={(e) => setFormData({ ...formData, pricePerInterval: e.target.value })}
            required
          />
          <Input
            id="quantity-available"
            label="Quantity Available"
            type="number"
            min="1"
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            required
            helperText="Simultaneous rentals allowed"
          />
        </div>

        {/* Advanced Settings - Only for Repeating Windows */}
        {formData.isRepeating && (
          <div className="border-t pt-6">
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex justify-between items-center w-full text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Advanced Settings</h3>
                    <svg
                      className={`w-5 h-5 text-gray-500 transform transition-transform ${
                        open ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </Disclosure.Button>
                  <Disclosure.Panel className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        id="repeat-start-date"
                        label="Repeat Start Date (Optional)"
                        type="date"
                        value={formData.repeatStartDate}
                        onChange={(e) =>
                          setFormData({ ...formData, repeatStartDate: e.target.value })
                        }
                        helperText="When to start repeating"
                      />
                      <Input
                        id="repeat-end-date"
                        label="Repeat End Date (Optional)"
                        type="date"
                        value={formData.repeatEndDate}
                        onChange={(e) => setFormData({ ...formData, repeatEndDate: e.target.value })}
                        helperText="When to stop repeating"
                      />
                    </div>

                    <DateExceptionsPicker
                      exceptions={formData.repeatExceptions}
                      onChange={(exceptions) =>
                        setFormData({ ...formData, repeatExceptions: exceptions })
                      }
                    />
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        )}

        {/* Conflicts Section */}
        {conflicts.length > 0 && (
          <div className="border-t pt-6">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Conflicting Windows Detected ({conflicts.length})
                  </h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    The following windows overlap with your current window configuration
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {conflicts.map((conflict, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {conflict.window.name || 'Unnamed Window'}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">{conflict.reason}</p>
                      <div className="text-xs text-gray-500 mt-2">
                        <span>Type: {conflict.window.windowType}</span>
                        {conflict.window.startTime && conflict.window.endTime && (
                          <span className="ml-3">
                            Time: {conflict.window.startTime} - {conflict.window.endTime}
                          </span>
                        )}
                        <span className="ml-3">Price: ${conflict.window.pricePerInterval}</span>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => openDeleteModal(conflict.window._id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex gap-4 border-t pt-6">
          <Button type="submit" isLoading={isLoading || checkingConflicts}>
            {mode === 'create' ? 'Create Window' : 'Save Changes'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/listings/${listingId}/windows`)}
          >
            Cancel
          </Button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setWindowToDelete(null);
        }}
        onConfirm={handleDeleteConflict}
        title="Delete Conflicting Window?"
        message="Are you sure you want to delete this conflicting window? This action cannot be undone. If this window has existing rentals, they may be affected."
        confirmText="Delete Window"
        cancelText="Cancel"
        variant="danger"
        isLoading={deletingConflict}
      />
    </>
  );
};

export default WindowForm;

